/**
 * Vista file della variante `stitch`: ogni elemento è una pillola in vetro.
 *
 * Cartelle e file stanno nella stessa lista, come nel mockup, ma la cartella si
 * riconosce prima di leggerne il nome: fondo azzurrino e barretta a sinistra.
 * Serve, perché è l'unica riga che *si apre* invece di scaricarsi.
 *
 * Ordinamento e permessi sono quelli delle altre grafiche: cambia la pelle, non
 * il comportamento.
 *
 * La selezione multipla vive qui solo come casella e stato della riga: chi
 * decide cosa e' selezionato, e cosa farci, e' DocumentsPage.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { isShortcut } from '../lib/shortcut.js';
import { extensionOf } from './FileChip.jsx';
import { formatDate, formatSize } from '../lib/format.js';
import type { Entry } from '../lib/types.js';
import { FolderGlyph } from './FolderGlyph.jsx';
import '../styles/stitch.css';

type SortKey = 'name' | 'size' | 'modifiedAt';
type SortDir = 'asc' | 'desc';

/** Icona Material e famiglia di colore per estensione. */
const KIND_BY_EXT: Record<string, { icon: string; family: string }> = {
  pdf: { icon: 'picture_as_pdf', family: 'pdf' },
  p7m: { icon: 'verified', family: 'firma' },
  xml: { icon: 'code', family: 'dati' },
  csv: { icon: 'table_chart', family: 'dati' },
  xlsx: { icon: 'table_chart', family: 'foglio' },
  xls: { icon: 'table_chart', family: 'foglio' },
  docx: { icon: 'description', family: 'testo' },
  doc: { icon: 'description', family: 'testo' },
  txt: { icon: 'description', family: 'testo' },
  jpg: { icon: 'image', family: 'img' },
  jpeg: { icon: 'image', family: 'img' },
  png: { icon: 'image', family: 'img' },
  zip: { icon: 'folder_zip', family: 'zip' },
  // Un `.url` non e' un documento ma un rimando: icona a catena, e la riga si
  // comporta come una cartella — si apre, non si scarica.
  url: { icon: 'link', family: 'link' },
};

const FALLBACK = { icon: 'draft', family: 'altro' };

export function StitchList({
  entries,
  selected,
  onToggleSelect,
  onToggleSelectAll,
  onOpenFolder,
  onOpenLink,
  onDelete,
}: {
  entries: Entry[];
  /** Ref dei file scelti. Le cartelle non si selezionano: vedi sotto. */
  selected: ReadonlySet<string>;
  onToggleSelect: (entry: Entry) => void;
  onToggleSelectAll: () => void;
  onOpenFolder: (entry: Entry) => void;
  /** Collegamenti `.url`: la destinazione la risolve il server. */
  onOpenLink: (entry: Entry) => void;
  onDelete: (entry: Entry) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => {
    const copy = [...entries];
    copy.sort((a, b) => {
      // Le cartelle restano sopra qualunque sia l'ordinamento: sono la
      // navigazione, non il contenuto.
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;

      let cmp = 0;
      if (sortKey === 'name') {
        cmp = a.name.localeCompare(b.name, 'it', { numeric: true, sensitivity: 'base' });
      } else if (sortKey === 'size') {
        cmp = (a.size ?? 0) - (b.size ?? 0);
      } else {
        cmp = (a.modifiedAt ?? '').localeCompare(b.modifiedAt ?? '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [entries, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'modifiedAt' ? 'desc' : 'asc');
    }
  };

  const arrow = (key: SortKey) => (key === sortKey ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  /*
   * Solo i file si selezionano, non le cartelle: le due azioni cumulative sono
   * scarica ed elimina, e una cartella non si scarica. Selezionarla per poterla
   * solo cancellare renderebbe la casella una trappola.
   */
  const selezionabili = entries.filter((e) => e.kind !== 'folder');
  const tutti = selezionabili.length > 0 && selezionabili.every((e) => selected.has(e.ref));
  const alcuni = !tutti && selezionabili.some((e) => selected.has(e.ref));

  /* `indeterminate` non esiste come attributo HTML: si scrive sul nodo. */
  const testata = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (testata.current) testata.current.indeterminate = alcuni;
  }, [alcuni]);

  if (entries.length === 0) {
    return (
      <div className="stlist__empty">
        <span className="material-symbols-outlined" aria-hidden="true">
          folder_open
        </span>
        <p>Questa cartella è vuota.</p>
      </div>
    );
  }

  return (
    <div className="stlist">
      <div className="stlist__head">
        <span className="stlist__pick">
          {selezionabili.length > 0 ? (
            <input
              ref={testata}
              type="checkbox"
              checked={tutti}
              onChange={onToggleSelectAll}
              aria-label={tutti ? 'Deseleziona tutti i file' : 'Seleziona tutti i file'}
            />
          ) : null}
        </span>
        <span />
        <button type="button" onClick={() => toggleSort('name')}>
          Nome{arrow('name')}
        </button>
        <button type="button" onClick={() => toggleSort('modifiedAt')}>
          Modificato{arrow('modifiedAt')}
        </button>
        <button type="button" onClick={() => toggleSort('size')}>
          Dimensione{arrow('size')}
        </button>
        <span className="visually-hidden">Azioni</span>
      </div>

      <ul className="stlist__rows">
        {sorted.map((entry) => {
          const folder = entry.kind === 'folder';
          // Il collegamento si apre solo se il cliente puo' leggerne il
          // contenuto: la destinazione sta dentro il file, e l'endpoint chiede
          // `download` esattamente come per un documento.
          const link = !folder && isShortcut(entry.name) && entry.perms.download;
          const look = folder
            ? { icon: 'folder', family: 'folder' }
            : (KIND_BY_EXT[extensionOf(entry.name)] ?? FALLBACK);

          return (
            <li
              key={entry.ref}
              className={`strow${folder ? ' strow--folder' : ''}${
                selected.has(entry.ref) ? ' is-selected' : ''
              }`}
            >
              {/* Anche per le cartelle la cella c'e': senza, le colonne della
                  riga scalerebbero di uno e la griglia si disallineerebbe. */}
              <span className="stlist__pick">
                {folder ? null : (
                  <input
                    type="checkbox"
                    checked={selected.has(entry.ref)}
                    onChange={() => onToggleSelect(entry)}
                    aria-label={`Seleziona ${entry.name}`}
                  />
                )}
              </span>

              <span className={`strow__icon strow__icon--${look.family}`} aria-hidden="true">
                {/* La cartella usa il glifo tracciato (contorno netto, interno
                    al 18%); il font Material è pieno o vuoto e non ha vie di
                    mezzo. I file restano sul font: lì il pieno va bene. */}
                {folder ? (
                  <FolderGlyph size={24} />
                ) : (
                  <span className="material-symbols-outlined is-filled">{look.icon}</span>
                )}
              </span>

              <span className="strow__name">
                {folder || link ? (
                  <button
                    type="button"
                    className="strow__label"
                    title={link ? `Apri il collegamento ${entry.name}` : entry.name}
                    onClick={() => (folder ? onOpenFolder(entry) : onOpenLink(entry))}
                  >
                    {entry.name}
                  </button>
                ) : (
                  <span className="strow__label" title={entry.name}>
                    {entry.name}
                  </span>
                )}
                {/* Su schermo stretto le colonne spariscono: qui il riassunto. */}
                <span className="strow__meta">
                  {formatDate(entry.modifiedAt)}
                  {folder ? '' : ` • ${formatSize(entry.size)}`}
                </span>
              </span>

              <span className="strow__date">{formatDate(entry.modifiedAt)}</span>
              <span className="strow__size">{folder ? '' : formatSize(entry.size)}</span>

              <span className="strow__actions">
                {folder ? (
                  <button
                    type="button"
                    className="strow__action"
                    title="Apri la cartella"
                    onClick={() => onOpenFolder(entry)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                    <span className="visually-hidden">Apri {entry.name}</span>
                  </button>
                ) : link ? (
                  /*
                    Al posto dello scarico: un `.url` sul disco si apre solo su
                    Windows, e da telefono non si apre affatto. Qui il gesto
                    utile e' uno solo, andare a destinazione.
                  */
                  <button
                    type="button"
                    className="strow__action"
                    onClick={() => onOpenLink(entry)}
                    title="Apri il collegamento"
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      open_in_new
                    </span>
                    <span className="visually-hidden">Apri {entry.name}</span>
                  </button>
                ) : entry.perms.download ? (
                  <a
                    className="strow__action"
                    href={api.fileUrl(entry.ref)}
                    download={entry.name}
                    title="Scarica"
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      download
                    </span>
                    <span className="visually-hidden">Scarica {entry.name}</span>
                  </a>
                ) : null}

                {/* `perms` arriva dal server per ogni item: qui si nasconde il
                    controllo, non si autorizza. La DELETE ricontrolla comunque. */}
                {entry.perms.delete ? (
                  <button
                    type="button"
                    className="strow__action strow__action--danger"
                    title="Elimina"
                    onClick={() => onDelete(entry)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      delete
                    </span>
                    <span className="visually-hidden">Elimina {entry.name}</span>
                  </button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
